<template>
  <div class="comparativo-grupos">
    <div
      class="is-flex is-justify-content-space-between is-align-items-center mb-4 cg-header-row"
    >
      <div>
        <h3 class="title is-5 mb-1">Comparativo de Grupos</h3>
        <p class="is-size-7 has-text-grey">
          Compara hasta 4 grupos (o el total del proceso) sobre el mismo
          radar de cultura.
        </p>
      </div>
      <div class="chart-toggle" v-if="selectedOptions.length">
        <div class="toggle-wrapper">
          <span
            class="toggle-item"
            :class="{ active: viewState === 'actual' }"
            @click="viewState = 'actual'"
          >
            Actual
          </span>
          <span class="divider">|</span>
          <span
            class="toggle-item"
            :class="{ active: viewState === 'deseada' }"
            @click="viewState = 'deseada'"
          >
            Deseada
          </span>
        </div>
      </div>
    </div>

    <div class="cg-chips">
      <span
        v-for="(opt, i) in selectedOptions"
        :key="opt.key"
        class="cg-chip"
        :style="{ borderColor: colorFor(i), color: colorFor(i) }"
      >
        <span class="cg-chip-dot" :style="{ background: colorFor(i) }"></span>
        {{ opt.label }}
        <span class="cg-chip-n" v-if="opt.key !== '__total__'"
          >n={{ opt.n }}</span
        >
        <button
          type="button"
          class="cg-chip-remove"
          @click="removeOption(opt.key)"
        >
          ✕
        </button>
      </span>

      <div class="cg-add-wrapper" ref="addWrapper" v-if="canAddMore">
        <button type="button" class="cg-add-btn" @click.stop="togglePicker">
          + Agregar grupo
        </button>
        <div v-if="pickerOpen" class="cg-panel">
          <input
            v-model="searchText"
            type="text"
            placeholder="Buscar por dependencia o subgrupo"
            class="cg-search"
            @click.stop
          />
          <div class="cg-options">
            <div
              v-if="totalOption && !isSelected('__total__')"
              class="cg-option"
              :class="{ 'cg-option-disabled': totalOption.disabled }"
              @click="!totalOption.disabled && toggleOption(totalOption)"
            >
              <span>Total (todos los encuestados)</span>
              <span class="cg-option-n">n={{ totalOption.n }}</span>
              <span v-if="totalOption.disabled" class="cg-option-warn"
                >⚠</span
              >
            </div>

            <div
              v-for="group in groupedAvailableOptions"
              :key="group.dependency"
              class="cg-group"
            >
              <div class="cg-group-label">{{ group.dependency }}</div>
              <div
                v-for="opt in group.options"
                :key="opt.key"
                class="cg-option"
                :class="{ 'cg-option-disabled': opt.disabled }"
                @click="!opt.disabled && toggleOption(opt)"
              >
                <span>{{ opt.subgrupo }}</span>
                <span class="cg-option-n">n={{ opt.n }}</span>
                <span v-if="opt.disabled" class="cg-option-warn">⚠</span>
              </div>
            </div>

            <div v-if="!hasAnyAvailable" class="cg-empty">
              No hay más grupos para agregar
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!selectedOptions.length" class="cg-placeholder">
      Selecciona uno o más grupos arriba para compararlos (máximo 4).
    </div>

    <div v-else class="cg-body">
      <div class="cg-radar-wrap">
        <ResultsChartMulti
          :series="chartSeries"
          :maxValue="chartMaxValue"
          :steps="20"
        />
      </div>

      <div class="cg-table-wrapper">
        <table class="cg-bar-table">
          <thead>
            <tr>
              <th class="cg-row-label"></th>
              <th
                v-for="(opt, i) in selectedOptions"
                :key="opt.key"
                v-tooltip="{
                  content: headerTooltip(opt),
                  classes: ['cg-header-tooltip'],
                  html: true,
                  placement: 'top',
                  boundariesElement: 'viewport',
                  popperOptions: { modifiers: { flip: { enabled: false } } },
                }"
              >
                <span class="cg-th-inner">
                  <span
                    class="cg-th-dot"
                    :style="{ background: colorFor(i) }"
                  ></span>
                  <span class="cg-th-label">{{
                    opt.key === "__total__" ? "Total" : opt.label
                  }}</span>
                  <span class="cg-th-n">n={{ opt.n }}</span>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in tableRows" :key="row.idx">
              <td class="cg-row-label" :style="{ color: row.color }">
                <span
                  class="cg-row-dot"
                  :style="{ background: row.color }"
                ></span>
                {{ row.label }}
              </td>
              <td
                v-for="opt in selectedOptions"
                :key="opt.key"
                class="cg-bar-cell"
              >
                <div
                  class="cg-bar-cont"
                  v-tooltip="{
                    content: barTooltip(row, opt),
                    classes: ['cg-bar-tooltip'],
                    html: true,
                    placement: 'top',
                    boundariesElement: 'viewport',
                    popperOptions: { modifiers: { flip: { enabled: false } } },
                  }"
                >
                  <div
                    class="cg-bar-fill"
                    :style="{
                      width: opt.actualValues[row.idx] + '%',
                      background: row.color,
                    }"
                  ></div>
                  <span
                    class="cg-bar-tick"
                    :style="{ left: opt.deseadaValues[row.idx] + '%' }"
                  ></span>
                  <span class="cg-bar-label"
                    >{{ opt.actualValues[row.idx].toFixed(1) }}%</span
                  >
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="cg-table-hint is-size-7 has-text-grey mt-2">
        Relleno = % Actual · línea punteada = % Deseada
      </p>
    </div>
  </div>
</template>

<script>
import ResultsChartMulti from "./ResultsChartMulti.vue";
import { COMPARISON_GROUP_COLORS } from "../../helpers";

const MAX_GROUPS = 4;
const CULTURE_ROWS = [
  { label: "Colaboración", idx: 0, color: "#6a00ce" },
  { label: "Creación", idx: 1, color: "#ff00ff" },
  { label: "Competencia", idx: 2, color: "#00d8d8" },
  { label: "Control", idx: 3, color: "#FECC0E" },
];

export default {
  name: "GroupComparison",

  components: { ResultsChartMulti },

  props: {
    dataGroups: {
      type: Array,
      required: true,
    },
    totalActual: {
      type: Array,
      required: true,
    },
    totalDeseada: {
      type: Array,
      required: true,
    },
    totalN: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      selectedKeys: [],
      viewState: "actual",
      searchText: "",
      pickerOpen: false,
      tableRows: CULTURE_ROWS,
    };
  },

  computed: {
    totalOption() {
      return {
        key: "__total__",
        label: "Total (todos los encuestados)",
        dependency: null,
        subgrupo: null,
        n: this.totalN,
        actualValues: this.totalActual,
        deseadaValues: this.totalDeseada,
        disabled: this.totalN < 5,
      };
    },
    groupOptions() {
      return this.dataGroups.map((d) => ({
        key: `${d.dependency}__${d.subgrupo}`,
        label:
          d.subgrupo && d.subgrupo !== d.dependency
            ? `${d.dependency} · ${d.subgrupo}`
            : d.dependency,
        dependency: d.dependency,
        subgrupo: d.subgrupo,
        n: d.n,
        actualValues: [d.clanActual, d.adhActual, d.merActual, d.jerActual],
        deseadaValues: [
          d.clanDeseada,
          d.adhDeseada,
          d.merDeseada,
          d.jerDeseada,
        ],
        disabled: d.n < 5,
      }));
    },
    allOptions() {
      return [this.totalOption, ...this.groupOptions];
    },
    selectedOptions() {
      return this.selectedKeys
        .map((k) => this.allOptions.find((o) => o.key === k))
        .filter(Boolean);
    },
    canAddMore() {
      return this.selectedOptions.length < MAX_GROUPS;
    },
    availableOptions() {
      const term = this.searchText.trim().toLowerCase();
      return this.groupOptions.filter((o) => {
        if (this.isSelected(o.key)) return false;
        if (!term) return true;
        return (
          (o.dependency || "").toLowerCase().includes(term) ||
          (String(o.subgrupo) || "").toLowerCase().includes(term)
        );
      });
    },
    groupedAvailableOptions() {
      const byDependency = {};
      this.availableOptions.forEach((o) => {
        if (!byDependency[o.dependency]) byDependency[o.dependency] = [];
        byDependency[o.dependency].push(o);
      });
      return Object.keys(byDependency)
        .sort()
        .map((dependency) => ({
          dependency,
          options: byDependency[dependency],
        }));
    },
    hasAnyAvailable() {
      const term = this.searchText.trim().toLowerCase();
      const totalMatches =
        !this.isSelected("__total__") &&
        (!term || "total".includes(term));
      return totalMatches || this.availableOptions.length > 0;
    },
    chartSeries() {
      return this.selectedOptions.map((opt, i) => ({
        key: opt.key,
        label: opt.label,
        color: this.colorFor(i),
        values:
          this.viewState === "actual" ? opt.actualValues : opt.deseadaValues,
      }));
    },
    chartMaxValue() {
      const allValues = this.selectedOptions.flatMap((o) => [
        ...o.actualValues,
        ...o.deseadaValues,
      ]);
      return Math.max(...allValues, 0);
    },
  },

  mounted() {
    document.addEventListener("click", this.onDocumentClick);
    document.addEventListener("keydown", this.onKeydown);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onDocumentClick);
    document.removeEventListener("keydown", this.onKeydown);
  },

  methods: {
    colorFor(index) {
      return (
        COMPARISON_GROUP_COLORS[index] ||
        COMPARISON_GROUP_COLORS[COMPARISON_GROUP_COLORS.length - 1]
      );
    },
    isSelected(key) {
      return this.selectedKeys.includes(key);
    },
    toggleOption(opt) {
      if (this.isSelected(opt.key)) {
        this.removeOption(opt.key);
        return;
      }
      if (!this.canAddMore) return;
      this.selectedKeys.push(opt.key);
      this.searchText = "";
      this.pickerOpen = false;
    },
    removeOption(key) {
      this.selectedKeys = this.selectedKeys.filter((k) => k !== key);
    },
    togglePicker() {
      this.pickerOpen = !this.pickerOpen;
    },
    onDocumentClick(event) {
      if (!this.pickerOpen) return;
      const wrapper = this.$refs.addWrapper;
      if (wrapper && !wrapper.contains(event.target)) {
        this.pickerOpen = false;
      }
    },
    onKeydown(event) {
      if (event.key === "Escape") this.pickerOpen = false;
    },
    escapeHtml(str) {
      return String(str)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    },
    headerTooltip(opt) {
      const name =
        opt.key === "__total__" ? "Total (todos los encuestados)" : opt.label;
      return (
        `<div class="cght-title">${this.escapeHtml(name)}</div>` +
        `<div class="cght-n">n = ${opt.n} encuestados</div>`
      );
    },
    barTooltip(row, opt) {
      const actual = opt.actualValues[row.idx];
      const deseada = opt.deseadaValues[row.idx];
      const isEmpty = actual === 0 && deseada === 0;
      const brecha = deseada - actual;
      const gapRow = isEmpty
        ? `<div class="cgbt-gap cgbt-gap-empty">—</div>`
        : `<div class="cgbt-row cgbt-gap ${brecha <= 0 ? "is-ok" : "is-pending"}">` +
          `<span>Brecha</span><span>${
            brecha <= 0 ? "Meta alcanzada" : `${brecha.toFixed(1)} pts por alcanzar`
          }</span></div>`;
      return (
        `<div class="cgbt-title">${this.escapeHtml(row.label)}</div>` +
        `<div class="cgbt-row"><span>Actual</span><span>${actual.toFixed(1)}%</span></div>` +
        `<div class="cgbt-row"><span>Deseado</span><span>${deseada.toFixed(1)}%</span></div>` +
        gapRow
      );
    },
  },
};
</script>

<style scoped>
.comparativo-grupos {
  --cg-accent: #660099;
}

.cg-header-row {
  flex-wrap: wrap;
  gap: 0.5rem;
}

.cg-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.cg-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1.5px solid;
  border-radius: 14px;
  padding: 4px 8px 4px 10px;
  font-size: 12px;
  font-weight: 600;
  background: #fff;
}

.cg-chip-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.cg-chip-n {
  color: #7a7a7a;
  font-weight: 400;
}

.cg-chip-remove {
  background: none;
  border: none;
  cursor: pointer;
  color: inherit;
  opacity: 0.6;
  font-size: 11px;
  padding: 0 2px;
}

.cg-chip-remove:hover {
  opacity: 1;
}

.cg-add-wrapper {
  position: relative;
}

.cg-add-btn {
  border: 1.5px dashed #c2c2c2;
  background: #fafafa;
  color: var(--cg-accent);
  border-radius: 14px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.cg-add-btn:hover {
  background: #f3e8ff;
  border-color: var(--cg-accent);
}

.cg-panel {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 50;
  width: 300px;
  max-height: 320px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(10, 10, 10, 0.12);
  padding: 8px;
}

.cg-search {
  width: 100%;
  padding: 6px 10px;
  font-size: 13px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  outline: none;
  margin-bottom: 6px;
}

.cg-search:focus {
  border-color: var(--cg-accent);
}

.cg-group-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #9a9a9a;
  padding: 6px 6px 2px;
}

.cg-option {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
}

.cg-option:hover {
  background: #f3e8ff;
}

.cg-option-n {
  margin-left: auto;
  color: #9a9a9a;
  font-size: 11px;
}

.cg-option-warn {
  color: #9a5a00;
}

.cg-option-disabled {
  color: #b5b5b5;
  cursor: not-allowed;
}

.cg-option-disabled:hover {
  background: none;
}

.cg-empty {
  padding: 10px 8px;
  font-size: 12px;
  color: #9a9a9a;
  text-align: center;
}

.cg-placeholder {
  border: 1px dashed #dbdbdb;
  border-radius: 8px;
  padding: 24px;
  text-align: center;
  color: #7a7a7a;
  font-size: 13px;
}

.cg-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

.cg-radar-wrap {
  width: 100%;
  max-width: 560px;
}

.cg-table-wrapper {
  width: 100%;
  overflow-x: auto;
}

.cg-bar-table {
  width: 100%;
  min-width: 480px;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 13px;
}

.cg-bar-table th,
.cg-bar-table td {
  padding: 8px 10px;
  text-align: left;
  border-bottom: 1px solid #f0f0f0;
  white-space: nowrap;
}

.cg-bar-table th {
  font-weight: 600;
  font-size: 12px;
  overflow: hidden;
}

.cg-bar-table th:not(.cg-row-label) {
  cursor: help;
  padding-left: 6px;
  padding-right: 6px;
}

.cg-th-inner {
  display: flex;
  align-items: center;
  gap: 4px;
}

.cg-th-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.cg-th-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.cg-th-n {
  color: #9a9a9a;
  font-weight: 400;
  font-size: 10px;
  white-space: nowrap;
  flex-shrink: 0;
}

.cg-row-label {
  width: 140px;
  font-weight: 600;
}

.cg-row-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 2px;
  margin-right: 6px;
}

.cg-bar-cont {
  position: relative;
  width: 100%;
  height: 24px;
  background: #f5f5f5;
  border-radius: 3px;
}

.cg-bar-fill {
  position: absolute;
  inset: 0 auto 0 0;
  height: 100%;
  border-radius: 3px;
  opacity: 0.45;
}

.cg-bar-tick {
  position: absolute;
  top: -3px;
  bottom: -3px;
  border-left: 2px dotted var(--cg-accent);
}

.cg-bar-label {
  position: absolute;
  left: 6px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 11px;
  font-weight: 700;
  color: #363636;
}

.cg-table-hint {
  display: block;
  text-align: center;
}
</style>

<!-- No scoped: v-tooltip renderiza en <body>. -->
<style>
.cg-header-tooltip {
  max-width: 240px !important;
}

.cg-header-tooltip .tooltip-inner {
  background: #2b2530;
  color: #fff;
  text-align: left;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #6b2fae;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
}

.cg-header-tooltip .tooltip-arrow {
  border-color: #2b2530;
}

.cg-header-tooltip .cght-title {
  font-weight: 700;
  font-size: 12.5px;
  color: #fff;
  margin-bottom: 2px;
}

.cg-header-tooltip .cght-n {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.75);
}

.cg-bar-tooltip {
  max-width: 220px !important;
}

.cg-bar-tooltip .tooltip-inner {
  background: #2b2530;
  color: #fff;
  text-align: left;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #6b2fae;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
}

.cg-bar-tooltip .tooltip-arrow {
  border-color: #2b2530;
}

.cg-bar-tooltip .cgbt-title {
  font-weight: 700;
  font-size: 12.5px;
  color: #fff;
  margin-bottom: 4px;
}

.cg-bar-tooltip .cgbt-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.85);
}

.cg-bar-tooltip .cgbt-gap {
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  font-weight: 700;
}

.cg-bar-tooltip .cgbt-gap.is-ok span:last-child {
  color: #7ee08b;
}

.cg-bar-tooltip .cgbt-gap.is-pending span:last-child {
  color: #f5c26b;
}

.cg-bar-tooltip .cgbt-gap-empty {
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
}
</style>



// WEBPACK FOOTER //
// src/components/cultura/sections/results/GroupComparison.vue